/**
 * Roll-up palette gate (Story 40-6, Task 3 / AC6 — ADR-RU-7).
 *
 * The aggregate-only allowlist for a ROLL-UP report's widget palette: the
 * SECOND line of defense behind the server engine (the engine is the
 * authoritative guard — `populateRollupWidgets` still rejects a non-aggregate
 * widget with an error envelope). This narrows the editor palette so the user
 * is only offered widgets the engine can actually aggregate across clients.
 *
 * SYNC: mirror the server source of truth in
 * `apps/server/src/services/rollup-populate.ts`:
 *   - `ROLLUP_CAPABLE_WIDGET_TYPES` = {kpi, line-chart, bar-chart, gauge, goal, rollup-table}
 *   - `STATIC_WIDGET_TYPES` = {text-block, cover-page, heading, paragraph, rich-text, spacer-divider, image, embed}
 * The web set is a SUBSET (never a superset) of the engine allowlist — it
 * intentionally OMITS `gauge` because gauge is preset-only on the web side
 * (there is no generic gauge palette item to offer; the engine still supports a
 * gauge widget if one exists, so this is a UX narrowing, not an engine change).
 *
 * Lives in its own leaf module (no React, no palette import) so both the palette
 * (WidgetPalette.tsx) and the editor route ($reportId.edit.tsx) can import it
 * without pulling the whole palette graph or risking an import cycle.
 */

import type { WidgetType } from "./WidgetPalette";

/**
 * Max members a roll-up may span (Story 40-6, AC3). SYNC: mirror the server cap
 * `ROLLUP_MEMBER_CAP` in `apps/server/src/services/rollup-populate.ts` (100). The
 * server re-validates `min(1)`/`max(cap)` — this is the UX guard so the portfolio
 * picker blocks beyond the cap before a doomed create. Mirrored (not imported)
 * because the web bundle cannot pull from the server package.
 */
export const ROLLUP_MEMBER_CAP = 100;

/** Aggregate base types whose single metric collapses cleanly across clients
 *  (the Roll-up Table renders per-client rows). Mirrors
 *  ROLLUP_CAPABLE_WIDGET_TYPES minus `gauge` (preset-only on web). */
const ROLLUP_AGGREGATE_BASE_TYPES: WidgetType[] = [
  "kpi",
  "line-chart",
  "bar-chart",
  "goal",
  "rollup-table",
];

/** Static-content widgets carry no data — passed through unchanged. Mirrors
 *  the server STATIC_WIDGET_TYPES set 1:1. */
const ROLLUP_STATIC_TYPES: WidgetType[] = [
  "text-block",
  "cover-page",
  "heading",
  "paragraph",
  "rich-text",
  "spacer-divider",
  "image",
  "embed",
];

/**
 * The full set of GENERIC widget base types a roll-up palette may offer:
 * aggregate base types ∪ static pass-through types. Per-platform PRESET items
 * (which bind a single `integrationId`) are excluded WHOLESALE elsewhere — a
 * preset is meaningless for a multi-client roll-up — so they are never tested
 * against this set.
 */
export const ROLLUP_ALLOWED_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([
  ...ROLLUP_AGGREGATE_BASE_TYPES,
  ...ROLLUP_STATIC_TYPES,
]);

/** True iff a generic palette item's base type aggregates in a roll-up. */
export function isRollupAllowedWidgetType(type: string): boolean {
  return ROLLUP_ALLOWED_WIDGET_TYPES.has(type as WidgetType);
}

/**
 * The member-union connected-platforms for a roll-up (Story 40-6, AC5): the
 * UNION of each member client's `platforms`. Pure + side-effect-free so both the
 * generate wizard and the editor route can derive `connectedPlatforms` from the
 * shared `["clients"]` cache without an extra fetch. A client not in `memberIds`
 * (or with no platforms) contributes nothing; the result order is insertion
 * order over the member list, de-duplicated.
 */
export function unionMemberPlatforms(
  memberIds: readonly string[],
  clients: ReadonlyArray<{ id: string; platforms?: string[] }>,
): string[] {
  if (memberIds.length === 0) return [];
  const memberSet = new Set(memberIds);
  const union = new Set<string>();
  for (const c of clients) {
    if (memberSet.has(c.id)) {
      for (const p of c.platforms ?? []) union.add(p);
    }
  }
  return [...union];
}
